<template>
	<div class="wrap_business_chart">
		<h4 v-if="props.date">{{ `${removeTimeFromDate(props.date)} 기준` }}</h4>
		<div class="chart_container">
			<canvas :id="chartId" ref="chartCanvas"></canvas>
			<div class="customLegend">
				<div v-for="(item, idx) in newLabels" :key="idx" class="customLegend_item">
					<div
						class="colorArea"
						:style="{ borderRadius: '999px', backgroundColor: props.data.datasets[0].backgroundColor[idx] }"
					></div>
					<div class="textArea">
						<span>{{ item }}</span
						><strong>{{ `${newData[idx].toFixed(2)}%` }}</strong>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, Ref, ref, watch } from 'vue';
import { Chart, ChartConfiguration, registerables } from 'chart.js';

import { removeTimeFromDate } from '@/utils/function/format';

Chart.register(...registerables);

type ChartData = {
	labels: string[];
	datasets: {
		data: number[];
		backgroundColor: string[];
		borderColor: string;
	}[];
};

const props = defineProps<{
	data: ChartData;
	date?: string;
}>();

const chartId = ref('chart-' + Math.random().toString(36).substring(2, 9));
const chartCanvas = ref<HTMLCanvasElement | null>(null);
const newLabels: Ref<string[]> = ref([]);
const newData: Ref<number[]> = ref([]);

let chartInstance: any;

const createChart = () => {
	if (chartCanvas.value) {
		// Destroy existing chart if it exists
		if (chartInstance) {
			chartInstance.destroy();
		}

		// Filter out zero values
		const filteredData = props.data.datasets[0].data.filter((value) => value !== 0);
		const filteredLabels = props.data.labels.filter((_, index) => props.data.datasets[0].data[index] !== 0);
		const filteredBackgroundColors = props.data.datasets[0].backgroundColor.filter(
			(_, index) => props.data.datasets[0].data[index] !== 0
		);

		const config: ChartConfiguration<'doughnut'> = {
			type: 'doughnut',
			data: {
				labels: filteredLabels,
				datasets: [
					{
						data: filteredData,
						backgroundColor: filteredBackgroundColors,
						borderColor: 'transparent',
					},
				],
			},
			options: {
				responsive: true,
				color: '#fff',
				plugins: {
					legend: {
						display: false,
					},
				},
				cutout: '50%',
			},
		};

		chartInstance = new Chart(chartCanvas.value, config);

		// Update legend data
		newData.value = filteredData.map((value) => Number(value.toFixed(2)));
		newLabels.value = filteredLabels;
	}
};

// Watch for changes in props.data
watch(
	() => props.data,
	() => {
		createChart();
	},
	{ deep: true }
);

onMounted(() => {
	createChart();
});

onUnmounted(() => {
	if (chartInstance) {
		chartInstance.destroy();
		chartInstance = null;
	}
});
</script>

<style scoped lang="scss">
.wrap_business_chart {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 1.6rem;
	h4 {
		font-size: var(--font-s-small);
		font-weight: var(--font-w-light);
		line-height: normal;
		color: var(--color-base-light);
	}
	.chart_container {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 2.4rem;
		canvas {
			max-width: 100%;
			width: 100%;
			max-height: 30rem;
			height: auto;
		}
		.customLegend {
			display: flex;
			flex-wrap: wrap;
			justify-content: center;
			align-items: center;
			gap: 1.6rem 3.2rem;
			.customLegend_item {
				display: flex;
				align-items: center;
				gap: 0.4rem;
				@media screen and (max-width: 768px) {
					margin: 0;
				}
				.colorArea {
					width: 3rem;
					height: 1.2rem;
				}
				.textArea {
					display: flex;
					flex-direction: row;
					gap: 1rem;
					font-size: var(--font-s-small);
					font-weight: var(--font-w-light);
					line-height: normal;
					margin: 0 auto;
					strong {
						font-weight: var(--font-w-semi-bold);
					}
				}
			}
		}
	}
	.getMoreBtn {
		margin: 0 auto;
		img {
			width: 2.8rem;
			height: 2.8rem;
		}
	}
}
</style>
